import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  clearLoginAttempts,
  redirectToBffLogin,
  redirectToBffLogout,
} from "../api/bffAuth";

const AUTH_BASE = process.env.REACT_APP_IDP_URL;

const ArgusAuthContext = createContext(null);

// Cookie-session auth over the BFF. Session state comes from the /auth/me probe; login and logout are
// top-level navigations to the BFF's OIDC endpoints (see api/bffAuth). There is no bearer token here.
export const ArgusAuthProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const response = await axios.get(`${AUTH_BASE}/auth/me`, {
          withCredentials: true,
          validateStatus: (status) => status === 200 || status === 401,
        });
        if (!active) return;
        if (response.status === 200) {
          clearLoginAttempts();
          const emailVerified = response.data?.emailVerified === true;
          setIsAuthenticated(true);
          setUser({
            argusId: response.data?.argusId ?? null,
            emailVerified,
            // snake_case alias for existing storefront consumers that read user.email_verified.
            email_verified: emailVerified,
          });
        } else {
          setIsAuthenticated(false);
          setUser(null);
        }
      } catch {
        if (active) {
          setIsAuthenticated(false);
          setUser(null);
        }
      } finally {
        if (active) setIsLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  // The BFF's OnRemoteFailure redirect lands back on the SPA origin with ?authError=retry|cancelled when an
  // OIDC callback fails (e.g. the user cancels the Auth0 prompt). Surface it once, then strip the param so a
  // refresh or share doesn't re-toast a stale error.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const authError = params.get("authError");
    if (!authError) return;
    const message =
      authError === "cancelled"
        ? "Sign-in was cancelled."
        : "Sign-in didn't complete. Please try again.";
    toast(message, { type: "error", autoClose: 6000 });
    params.delete("authError");
    const query = params.toString();
    const newUrl =
      window.location.pathname +
      (query ? `?${query}` : "") +
      window.location.hash;
    window.history.replaceState(null, "", newUrl);
  }, []);

  const loginWithRedirect = useCallback((options) => {
    const screenHint =
      options?.screenHint ?? options?.authorizationParams?.screen_hint;
    const returnTo = options?.returnTo ?? options?.appState?.returnTo;
    redirectToBffLogin({ screenHint, returnTo });
  }, []);

  const logout = useCallback(() => {
    redirectToBffLogout();
    return Promise.resolve();
  }, []);

  const value = useMemo(
    () => ({
      isAuthenticated,
      isLoading,
      user,
      loginWithRedirect,
      logout,
    }),
    [
      isAuthenticated,
      isLoading,
      user,
      loginWithRedirect,
      logout,
    ],
  );

  return (
    <ArgusAuthContext.Provider value={value}>
      {children}
    </ArgusAuthContext.Provider>
  );
};

export const useArgusAuthContext = () => {
  const context = useContext(ArgusAuthContext);
  if (context === null) {
    throw new Error("useArgusAuth must be used within an ArgusAuthProvider");
  }
  return context;
};
